import React from 'react'

import SelectPendencyStatus from '../SelectPendencyStatus'
import SupportText from 'presentation/shared/components/SupportText'
import Chip from 'presentation/shared/components/Chip'
import TextArea from 'presentation/shared/components/TextArea'
import Button from 'presentation/shared/components/Button'
import { Immunization } from 'domain/entities/immunization-model'

import * as S from './styles'
import { DoctorRegistrationSteps } from 'common/enum/doctor-overall-registration-steps'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { DoctorRegisterStepStatus } from 'domain/entities/doctor-register'
import {
  disableObservation,
  generateNewRegistrationDetailsValidationSchema
} from 'presentation/hospital/layouts/DoctorRegister/NewRegistrationsDetails'
import { observer } from 'mobx-react'
import { newRegistrationDetailsContext } from 'presentation/hospital/layouts/DoctorRegister/NewRegistrationsDetails/context'

type ImmunizationsProps = {
  immunizations?: Immunization[]
  initialValues: registerPendencyValues
  updateStepStatus?: (step: any, initialValues: any) => Promise<void>
}

const Immunizations = ({
  immunizations,
  initialValues = {} as registerPendencyValues,
  updateStepStatus
}: ImmunizationsProps) => {
  const { isStepsUpdateBlocked } = newRegistrationDetailsContext
  const { handleChange, values, setFieldValue, handleSubmit, isValid } =
    useFormik({
      initialValues,
      validateOnMount: true,
      enableReinitialize: true,
      validationSchema: generateNewRegistrationDetailsValidationSchema(
        DoctorRegistrationSteps.IMUNNIZATION
      ),
      onSubmit: async (values) => {
        try {
          await updateStepStatus?.(values, initialValues)
          toast.success('Dados enviados com sucesso.')
          window.scrollTo(0, 0)
        } catch (error) {
          toast.error('Erro ao salvar dados.')
        }
      }
    })

  const setPendencyStatus = (field: string, event: any) => {
    setFieldValue(
      `${field}.status`,
      event.target.value === 'Pendente'
        ? DoctorRegisterStepStatus.PENDENT
        : DoctorRegisterStepStatus.APPROVED_DOCTOR_REGISTER
    )
  }

  const getImmunizationLabel = (immunization: string) => {
    let immunizationLabel = ''
    switch (immunization) {
      case 'hepatitis-b':
        immunizationLabel = 'hepatite B'
        break
      case 'dual-adult':
        immunizationLabel = 'dupla adulto'
        break
      case 'influenza':
        immunizationLabel = 'influenza'
        break
      case 'yellow-fever':
        immunizationLabel = 'febre amarela'
        break
      case 'measles':
        immunizationLabel = 'sarampo'
        break
      case 'covid19':
        immunizationLabel = 'COVID-19'
        break
      default:
        break
    }
    return immunizationLabel
  }

  return (
    <S.Wrapper>
      <S.PendencySelection>
        <SelectPendencyStatus
          handleChange={(e) =>
            setPendencyStatus(DoctorRegistrationSteps.IMUNNIZATION, e)
          }
          initialValue={values[DoctorRegistrationSteps.IMUNNIZATION].status}
          data-testid="select-pendency-status-immunizations"
          disabled={isStepsUpdateBlocked}
        />
      </S.PendencySelection>
      <S.Title>Imunização</S.Title>
      <S.ImunnizationsContainer>
        <S.InputsContainer>
          {immunizations &&
            immunizations.map((immunization) => (
              <div key={immunization.description}>
                <SupportText data-testid="text-immunization" color="primary">
                  {`Imunização contra ${getImmunizationLabel(
                    immunization.description
                  )}`}
                </SupportText>
                <S.ChipsContainer>
                  {chipOptions.map((chip) => (
                    <Chip
                      key={chip.label}
                      label={chip.label}
                      customValue={chip.value}
                      name={`${immunization.description}-${chip.label}`}
                      checked={chip.value === immunization.immunized}
                    />
                  ))}
                </S.ChipsContainer>
              </div>
            ))}
        </S.InputsContainer>
        <S.PendencyJustification>
          <TextArea
            data-testid="input-pendency-immunizations"
            name={`${DoctorRegistrationSteps.IMUNNIZATION}.description`}
            label="No caso de pendência, favor descrever"
            value={values[DoctorRegistrationSteps.IMUNNIZATION].description}
            onChange={handleChange}
            disabled={
              disableObservation(DoctorRegistrationSteps.IMUNNIZATION) ||
              values[DoctorRegistrationSteps.IMUNNIZATION].status !==
                DoctorRegisterStepStatus.PENDENT ||
              isStepsUpdateBlocked
            }
            required
          />
        </S.PendencyJustification>
        <S.ActionsButtons>
          <Button
            data-testid="btn-save-immunizations"
            onClick={() => handleSubmit()}
            variant={isValid && !isStepsUpdateBlocked ? 'outlined' : undefined}
            disabled={!isValid || isStepsUpdateBlocked}
          >
            Salvar
          </Button>
        </S.ActionsButtons>
      </S.ImunnizationsContainer>
    </S.Wrapper>
  )
}

const chipOptions = [
  {
    label: 'Sim',
    value: true
  },
  {
    label: 'Não',
    value: false
  }
]

type registerPendencyValues = {
  [DoctorRegistrationSteps.IMUNNIZATION]: {
    description?: string
    status: DoctorRegisterStepStatus
  }
}

export default observer(Immunizations)
